/**
 * Attachment Removal Dialog
 *
 * Smart dialog for selectively removing attachments.
 * Shows token estimates for each attachment to help users decide.
 */

import { Check, FileText, Image } from 'lucide-react';
import React, { useState } from 'react';
import type { DraftAttachment } from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { estimateAttachmentTokens, formatTokenCount } from '../utils/token-estimation';
import { Button } from './button';
import { Dialog } from './dialog';
import { IconButton } from './icon-button';

export type AttachmentRemovalDialogProps = {
  isOpen: boolean;
  attachments: DraftAttachment[];
  onConfirm: (selectedIds: string[]) => void;
  onCancel: () => void;
  className?: string;
};

export const AttachmentRemovalDialog: React.FC<AttachmentRemovalDialogProps> = ({
  isOpen,
  attachments,
  onConfirm,
  onCancel,
  className,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset selection when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(attachments.map(a => a.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const totalTokens = attachments.reduce((sum, a) => sum + estimateAttachmentTokens(a), 0);
  const selectedTokens = attachments
    .filter(a => selectedIds.has(a.id))
    .reduce((sum, a) => sum + estimateAttachmentTokens(a), 0);
  const remainingTokens = totalTokens - selectedTokens;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      size="md"
      aria-labelledby="attachment-removal-title"
      className={cn('max-w-lg max-h-[80vh] flex flex-col', className)}
    >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b">
          <div>
            <h2 id="attachment-removal-title" className="text-lg font-semibold text-foreground">Remove Attachments</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Select files to remove from this message
            </p>
          </div>
          <IconButton
            iconName="X"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            aria-label="Close dialog"
          />
        </div>

        {/* Content - Scrollable list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-2">
          {attachments.map(attachment => {
            const isSelected = selectedIds.has(attachment.id);
            const estimatedTokens = estimateAttachmentTokens(attachment);
            const mimeType = attachment.contentType;
            const isImage = mimeType?.startsWith('image/') || attachment.isScreenshot;

            return (
              <button
                key={attachment.id}
                onClick={() => toggleSelection(attachment.id)}
                className={cn(
                  'w-full text-left rounded-lg border p-3 transition-colors',
                  'hover:bg-accent/50',
                  isSelected
                    ? 'bg-destructive/10 border-destructive'
                    : 'bg-background border-border'
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div
                    className={cn(
                      'w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5',
                      isSelected
                        ? 'bg-destructive border-destructive'
                        : 'bg-background border-border'
                    )}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 text-destructive-foreground" />}
                  </div>

                  {/* Icon */}
                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                    {isImage ? (
                      <Image className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <FileText className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium text-foreground truncate"
                      title={attachment.name}
                    >
                      {attachment.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {attachment.file && (
                        <span className="text-xs text-muted-foreground">
                          {(attachment.file.size / 1024).toFixed(0)} KB
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs font-medium text-muted-foreground tabular-nums">
                        ~{formatTokenCount(estimatedTokens)} tokens
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t p-6 space-y-4">
          {/* Token summary */}
          <div className="bg-muted rounded-lg p-3 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Current total:</span>
              <span className="font-semibold text-foreground tabular-nums">
                {formatTokenCount(totalTokens)} tokens
              </span>
            </div>
            {selectedIds.size > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">To remove:</span>
                  <span className="font-semibold text-destructive tabular-nums">
                    -{formatTokenCount(selectedTokens)} tokens
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-foreground font-medium">After removal:</span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {formatTokenCount(remainingTokens)} tokens
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={selectAll}
              className="text-primary hover:underline"
              disabled={selectedIds.size === attachments.length}
            >
              Select all
            </button>
            <span className="text-muted-foreground">•</span>
            <button
              onClick={deselectAll}
              className="text-primary hover:underline"
              disabled={selectedIds.size === 0}
            >
              Deselect all
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={selectedIds.size === 0}
              className="flex-1"
            >
              Remove {selectedIds.size > 0 && `(${selectedIds.size})`}
            </Button>
            <Button variant="outline" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
    </Dialog>
  );
};

AttachmentRemovalDialog.displayName = 'AttachmentRemovalDialog';
